import { globalEventBus } from '../events'
import { PageBlockDefinition, PageBlockEntry, PageBlockMetadata } from './types'

export class PageBlockRegistry {
  private cache: {
    [packageName: string]: {
      [name: string]: PageBlockDefinition
    }
  } = {}

  private clone<T>(value: T): T {
    return structuredClone(value)
  }

  registerBlock(
    packageName: string,
    name: string,
    config: Record<string, unknown>,
    metadata: PageBlockMetadata,
  ): void {
    this.cache[packageName] = this.cache[packageName] ?? {}

    this.cache[packageName][name] = {
      config: this.clone(config),
      metadata: this.clone(metadata),
    }

    globalEventBus.emit({
      topic: 'horizon:pageBlock:registered:v1',
      payload: {
        package: packageName,
        name,
      },
    })
  }

  getBlock(packageName: string, name: string): PageBlockEntry | null {
    const block = this.cache[packageName]?.[name]
    if (!block) return null

    return {
      package: packageName,
      name,
      config: this.clone(block.config),
      metadata: this.clone(block.metadata),
    }
  }

  /**
   * List all registered page blocks, optionally filtered by tags.
   * Tag filtering follows the same semantics as ComponentLoader.listComponents:
   * blocks with no supports array pass through all filters,
   * blocks with supports must have at least one matching tag.
   */
  listBlocks(filter?: { tags?: string[] | null }): PageBlockEntry[] {
    const entries: PageBlockEntry[] = []

    for (const packageName of Object.keys(this.cache)) {
      for (const name of Object.keys(this.cache[packageName])) {
        const block = this.cache[packageName][name]
        entries.push({
          package: packageName,
          name,
          config: this.clone(block.config),
          metadata: this.clone(block.metadata),
        })
      }
    }

    const tags = filter?.tags
    if (!tags || tags.length === 0) {
      return entries
    }

    return entries.filter((entry) => {
      const supports = entry.metadata.supports
      if (!supports || supports.length === 0) {
        return true
      }
      return supports.some((tag) => tags.includes(tag))
    })
  }
}

export const pageBlockRegistry = new PageBlockRegistry()
